import React, { useCallback, useEffect, useState, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { PlayIcon, PauseIcon, Volume2Icon, Loader2Icon } from 'lucide-react';
function formatTime(seconds: number): string {
  if (!isFinite(seconds) || seconds < 0) return '0:00';
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}
const AUDIO_SRC = 'https://pub-db58a636b24b488086090ea7bd0d2898.r2.dev/SampleAudioBook.MP3';
export function AudioPlayer() {
  const containerRef = useRef<HTMLDivElement>(null);
  const audioRef = useRef<HTMLAudioElement>(null);
  const progressRef = useRef<HTMLDivElement>(null);
  const isInView = useInView(containerRef, {
    once: true,
    margin: '-60px'
  });
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isLoaded, setIsLoaded] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    const onLoadedMetadata = () => {
      setDuration(audio.duration);
      setIsLoaded(true);
      setIsLoading(false);
    };
    const onCanPlay = () => {
      setIsLoading(false);
      setIsLoaded(true);
    };
    const onTimeUpdate = () => {
      setCurrentTime(audio.currentTime);
    };
    const onEnded = () => {
      setIsPlaying(false);
      setCurrentTime(0);
    };
    const onWaiting = () => {
      setIsLoading(true);
    };
    const onPlaying = () => {
      setIsLoading(false);
      setIsPlaying(true);
    };
    audio.addEventListener('loadedmetadata', onLoadedMetadata);
    audio.addEventListener('canplay', onCanPlay);
    audio.addEventListener('timeupdate', onTimeUpdate);
    audio.addEventListener('ended', onEnded);
    audio.addEventListener('waiting', onWaiting);
    audio.addEventListener('playing', onPlaying);
    return () => {
      audio.removeEventListener('loadedmetadata', onLoadedMetadata);
      audio.removeEventListener('canplay', onCanPlay);
      audio.removeEventListener('timeupdate', onTimeUpdate);
      audio.removeEventListener('ended', onEnded);
      audio.removeEventListener('waiting', onWaiting);
      audio.removeEventListener('playing', onPlaying);
    };
  }, []);
  const togglePlay = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    if (isPlaying) {
      audio.pause();
      setIsPlaying(false);
    } else {
      setIsLoading(true);
      audio.play().then(() => {
        setIsPlaying(true);
        setIsLoading(false);
      }).catch(() => {
        setIsLoading(false);
      });
    }
  }, [isPlaying]);
  const handleSeek = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    const audio = audioRef.current;
    const bar = progressRef.current;
    if (!audio || !bar || !duration) return;
    const rect = bar.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const fraction = Math.max(0, Math.min(1, clickX / rect.width));
    audio.currentTime = fraction * duration;
    setCurrentTime(audio.currentTime);
  }, [duration]);
  const progress = duration > 0 ? currentTime / duration * 100 : 0;
  return <div ref={containerRef} className="w-full" data-id="element-1">
      <motion.div initial={{
      opacity: 0,
      y: 24
    }} animate={isInView ? {
      opacity: 1,
      y: 0
    } : {}} transition={{
      duration: 0.7
    }} className="w-full" data-id="element-2">
        {/* Header Label */}
        <div className="flex items-center gap-2 mb-4 text-teal-400 font-bold tracking-widest text-xs uppercase" data-id="element-3">
          <PlayIcon className="w-3 h-3 fill-current" data-id="element-4" />
          <span data-id="element-5">Listen to a sample</span>
        </div>

        {/* Player Card */}
        <div className="relative bg-[#0f1111] rounded-2xl p-5 border border-white/5 shadow-xl shadow-black/50" data-id="element-6">
          {/* Volume Icon (Top Left) */}
          <div className="absolute top-5 left-5" data-id="element-7">
            <Volume2Icon className="w-4 h-4 text-teal-500/70" data-id="element-8" />
          </div>

          <div className="flex items-center gap-4 mt-6 mb-2" data-id="element-9">
            {/* Play Button */}
            <button onClick={togglePlay} aria-label={isPlaying ? 'Pause audio' : 'Play audio'} className="group relative flex-shrink-0 w-14 h-14 rounded-full bg-gradient-to-br from-teal-400 to-cyan-500 flex items-center justify-center shadow-lg shadow-teal-500/20 hover:shadow-teal-500/40 hover:scale-105 active:scale-95 transition-all duration-300" data-id="element-10">
              {/* Inner glow ring */}
              <div className="absolute inset-0 rounded-full bg-gradient-to-br from-white/20 to-transparent pointer-events-none" data-id="element-11" />

              {isLoading ? <Loader2Icon className="w-6 h-6 text-black relative z-10 animate-spin" data-id="element-12" /> : isPlaying ? <PauseIcon className="w-6 h-6 text-black relative z-10 fill-black" data-id="element-13" /> : <PlayIcon className="w-6 h-6 text-black relative z-10 ml-1 fill-black" data-id="element-14" />}
            </button>

            {/* Progress & Time */}
            <div className="flex-1 min-w-0 pt-1" data-id="element-15">
              {/* Equalizer Bars (Above Progress Bar) */}
              <div className="flex items-end gap-[2px] h-3 mb-1.5 ml-0.5" data-id="element-16">
                <div className={`eq-bar ${isPlaying ? 'active' : ''}`} style={{
                '--eq-max-h': '10px',
                '--eq-speed': '0.5s',
                '--eq-delay': '0s',
                width: '2px'
              } as React.CSSProperties} data-id="element-17" />
                <div className={`eq-bar ${isPlaying ? 'active' : ''}`} style={{
                '--eq-max-h': '8px',
                '--eq-speed': '0.7s',
                '--eq-delay': '0.15s',
                width: '2px'
              } as React.CSSProperties} data-id="element-18" />
                <div className={`eq-bar ${isPlaying ? 'active' : ''}`} style={{
                '--eq-max-h': '12px',
                '--eq-speed': '0.45s',
                '--eq-delay': '0.3s',
                width: '2px'
              } as React.CSSProperties} data-id="element-19" />
                <div className={`eq-bar ${isPlaying ? 'active' : ''}`} style={{
                '--eq-max-h': '6px',
                '--eq-speed': '0.65s',
                '--eq-delay': '0.1s',
                width: '2px'
              } as React.CSSProperties} data-id="element-20" />
                <div className={`eq-bar ${isPlaying ? 'active' : ''}`} style={{
                '--eq-max-h': '9px',
                '--eq-speed': '0.55s',
                '--eq-delay': '0.25s',
                width: '2px'
              } as React.CSSProperties} data-id="element-21" />
              </div>

              {/* Progress Bar */}
              <div ref={progressRef} onClick={handleSeek} className="group relative w-full h-1.5 bg-neutral-800 rounded-full cursor-pointer overflow-hidden mb-2" role="slider" aria-label="Audio progress" aria-valuenow={Math.round(currentTime)} aria-valuemin={0} aria-valuemax={Math.round(duration)} data-id="element-22">
                {/* Filled track */}
                <div className="absolute inset-y-0 left-0 bg-gradient-to-r from-teal-400 to-cyan-400 rounded-full transition-[width] duration-100 ease-linear" style={{
                width: `${progress}%`
              }} data-id="element-23" />
                {/* Hover indicator */}
                <div className="absolute top-1/2 -translate-y-1/2 w-2.5 h-2.5 rounded-full bg-white shadow-md shadow-teal-500/30 opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none" style={{
                left: `calc(${progress}% - 5px)`
              }} data-id="element-24" />
              </div>

              {/* Time Display */}
              <div className="flex items-center justify-between text-[10px] font-mono text-neutral-500" data-id="element-25">
                <span data-id="element-26">{formatTime(currentTime)}</span>
                <span data-id="element-27">{isLoaded ? formatTime(duration) : '--:--'}</span>
              </div>
            </div>
          </div>

          {/* Footer Text */}
          <div className="text-center mt-4" data-id="element-28">
            <p className="text-neutral-600 text-[10px] font-medium tracking-wide uppercase" data-id="element-29">
              Total Duration: 1 hour 50 minutes
            </p>
          </div>

          {/* Audio Element */}
          <audio ref={audioRef} src={AUDIO_SRC} preload="metadata" data-id="element-30" />
        </div>
      </motion.div>
    </div>;
}